import React, { useState, useCallback } from "react";
import { Table, Typography, Spin, Tag, Button, Modal, InputNumber, DatePicker, Space, Popconfirm, Toast, Checkbox, Input, Select } from "@douyinfe/semi-ui";
import type { TagColor } from "@douyinfe/semi-ui/lib/es/tag";
import { IconRefresh, IconSetting, IconSearch } from "@douyinfe/semi-icons";
import { formatAmountZhCn } from "../../utils";
import { ApiClient } from "../../api/ApiClient";
import dayjs from "dayjs";

const apiClient = ApiClient.getDefault();
const { Text } = Typography;

const ANOMALY_SCENARIOS: { type: string; label: string; color: TagColor; desc: string }[] = [
  { type: "AMOUNT_MISMATCH", label: "金额偏差", color: "red", desc: "计划总额与贷款金额的偏差超过阈值百分比" },
  { type: "OVERDUE", label: "还款逾期", color: "red", desc: "还款中的计划超过应还日期N天仍未还款" },
  { type: "EARLY_PAYMENT", label: "提前还款", color: "blue", desc: "实还日期早于应还日期超过N天" },
  { type: "DATE_DISORDER", label: "日期乱序", color: "orange", desc: "按期号排序后，前一期应还日期晚于后一期" },
  { type: "PAYMENT_SKIP", label: "还款跳期", color: "red", desc: "前期未还但后期已还，存在跳过中间期还款的情况" },
  { type: "PLAN_DUPLICATE", label: "计划重复", color: "red", desc: "同一贷款下存在期号或应还日期重复的计划" },
  { type: "SINGLE_PERIOD_DEVIATION", label: "单期偏差", color: "orange", desc: "单期实还金额与应还金额偏差超过阈值百分比" },
  { type: "STATUS_ANOMALY", label: "状态异常", color: "red", desc: "贷款已结清但仍存在还款中或逾期的计划" },
  { type: "SETTLED_AMOUNT_MISMATCH", label: "结清金额不匹配", color: "red", desc: "已结清贷款的实还总额与贷款金额偏差超过阈值" },
  { type: "UNSETTLED_OVERPAY", label: "未结清多还", color: "orange", desc: "未结清贷款的实还总额已超过贷款金额一定比例" },
  { type: "UNSETTLED_UNDERPAY", label: "未结清少还", color: "orange", desc: "最后一期已还但总额低于贷款金额一定比例" },
];

const ANOMALY_CONFIG: Record<string, { label: string; color: TagColor }> = {};
ANOMALY_SCENARIOS.forEach(s => { ANOMALY_CONFIG[s.type] = { label: s.label, color: s.color }; });

const LOAN_TYPE_MAP: Record<string, string> = { MORTGAGE: "信贷", CARD: "信用卡", BORROW: "借款" };
const LOAN_STATUS_MAP: Record<string, { text: string; color: TagColor }> = {
  HKZ: { text: "还款中", color: "blue" }, YJQ: { text: "已结清", color: "green" },
  YQ: { text: "已逾期", color: "red" }, 
};
const PLAN_STATUS_MAP: Record<string, { text: string; color: TagColor }> = {
  HKZ: { text: "还款中", color: "blue" }, YJQ: { text: "已还款", color: "green" },
  YQ: { text: "已逾期", color: "red" }, DHK: { text: "待还款", color: "grey" },
};

interface AnomalyItem {
  type: string; severity: string; message: string; suggestion: string; planIds?: number[];
}
interface PlanRow {
  id: number; periodNo: number; dueDate: string; oneTermAmount: number;
  paidDate: string; paidAmount: number; postingAmount: number; status: string;
  anomalyTypes?: string[];
}
interface LoanRow {
  loanId: number; loanNo: string; loanType: string; loanAmount: number;
  planTotalAmount: number; paidTotalAmount: number; leadName: string;
  loanStatus: string; platform: string; anomalies: AnomalyItem[]; plans: PlanRow[];
}

export default function LoanAnomalyMonitor() {
  const [data, setData] = useState<LoanRow[]>([]);
  const [loading, setLoading] = useState(false);
  // search state
  const [sLeadId, setSLeadId] = useState<string>("");
  const [sLeadName, setSLeadName] = useState<string>("");
  const [sLoanNo, setSLoanNo] = useState<string>("");
  const [sLoanType, setSLoanType] = useState<string>("");
  const [sAgentName, setSAgentName] = useState<string>("");
  const [sDueDateRange, setSDueDateRange] = useState<[Date, Date]>(() => [
    dayjs().subtract(6, "month").startOf("month").toDate(),
    dayjs().endOf("month").toDate(),
  ]);
  const [sPaidDateRange, setSPaidDateRange] = useState<[Date, Date] | undefined>(undefined);
  const [thresholdVisible, setThresholdVisible] = useState(false);
  const [thresholds, setThresholds] = useState<Record<string, number>>({});
  const [enabledTypes, setEnabledTypes] = useState<string[]>([]);
  const [editPlanVisible, setEditPlanVisible] = useState(false);
  const [addPlanVisible, setAddPlanVisible] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<any>(null);
  const [currentLoanId, setCurrentLoanId] = useState<number>(0);
  // edit plan form state
  const [editOneTermAmount, setEditOneTermAmount] = useState<number | undefined>(undefined);
  const [editDueDate, setEditDueDate] = useState<Date | undefined>(undefined);
  const [editPaidAmount, setEditPaidAmount] = useState<number | undefined>(undefined);
  const [editPaidDate, setEditPaidDate] = useState<Date | undefined>(undefined);
  // add plan form state
  const [addPeriodNo, setAddPeriodNo] = useState<number | undefined>(undefined);
  const [addOneTermAmount, setAddOneTermAmount] = useState<number | undefined>(undefined);
  const [addDueDate, setAddDueDate] = useState<Date | undefined>(undefined);

  const detect = useCallback(() => {
    setLoading(true);
    const params: Record<string, any> = {};
    if (sLeadId) params.leadId = sLeadId;
    if (sLeadName) params.leadName = sLeadName;
    if (sLoanNo) params.loanNo = sLoanNo;
    if (sLoanType) params.loanType = sLoanType;
    if (sAgentName) params.agentName = sAgentName;
    if (sDueDateRange) {
      params["dueDate"] = dayjs(sDueDateRange[0]).format("YYYY-MM-DD") + "," + dayjs(sDueDateRange[1]).format("YYYY-MM-DD");
    }
    if (sPaidDateRange) {
      params["paidDate"] = dayjs(sPaidDateRange[0]).format("YYYY-MM-DD") + "," + dayjs(sPaidDateRange[1]).format("YYYY-MM-DD");
    }
    apiClient.request("/loanAnomaly/detect", "GET", params).then((resp: any) => {
      // 为每个计划标注异常类型
      const rows = (resp.data || []).map((loan: LoanRow) => {
        const planAnomalyMap = new Map<number, string[]>();
        loan.anomalies.forEach((a: AnomalyItem) => {
          (a.planIds || []).forEach((pid: number) => {
            if (!planAnomalyMap.has(pid)) planAnomalyMap.set(pid, []);
            planAnomalyMap.get(pid)!.push(a.type);
          });
        });
        return {
          ...loan,
          plans: loan.plans.map((p: PlanRow) => ({ ...p, anomalyTypes: planAnomalyMap.get(p.id) || [] })),
        };
      });
      setData(rows);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [sLeadId, sLeadName, sLoanNo, sLoanType, sAgentName, sDueDateRange, sPaidDateRange]);

  const loadThresholds = useCallback(() => {
    Promise.all([
      apiClient.request("/loanAnomaly/thresholds", "GET"),
      apiClient.request("/loanAnomaly/enabledTypes", "GET"),
    ]).then(([threshResp, enabledResp]: any[]) => {
      setThresholds(threshResp.data || {});
      setEnabledTypes((enabledResp.data || []) as string[]);
      setThresholdVisible(true);
    });
  }, []);

  const saveThresholds = useCallback(() => {
    Promise.all([
      apiClient.request("/loanAnomaly/thresholds", "PUT", null, thresholds),
      apiClient.request("/loanAnomaly/enabledTypes", "PUT", null, enabledTypes),
    ]).then(() => {
      Toast.success("配置已更新");
      setThresholdVisible(false);
    });
  }, [thresholds, enabledTypes]);

  const deleteLoan = useCallback((loanId: number) => {
    apiClient.request(`/mortgageLoan/delete/${loanId}`, "DELETE").then(() => {
      Toast.success("贷款已删除");
      detect();
    });
  }, [detect]);

  const deletePlan = useCallback((planId: number) => {
    apiClient.request("/repaymentPlan/delete", "POST", { id: planId }).then(() => {
      Toast.success("计划已删除");
      detect();
    });
  }, [detect]);

  const openEditPlan = useCallback((plan: any) => {
    setCurrentPlan(plan);
    setEditOneTermAmount(plan.oneTermAmount);
    setEditDueDate(plan.dueDate ? dayjs(plan.dueDate).toDate() : undefined);
    setEditPaidAmount(plan.paidAmount);
    setEditPaidDate(plan.paidDate ? dayjs(plan.paidDate).toDate() : undefined);
    setEditPlanVisible(true);
  }, []);

  const saveEditPlan = useCallback(() => {
    const params: any = { id: currentPlan.id };
    if (editOneTermAmount != null) params.oneTermAmount = editOneTermAmount;
    if (editDueDate) params.dueDate = dayjs(editDueDate).format("YYYY-MM-DD");
    if (editPaidAmount != null) params.paidAmount = editPaidAmount;
    if (editPaidDate) params.paidDate = dayjs(editPaidDate).format("YYYY-MM-DD");
    apiClient.request("/repaymentPlan/modifyRepaymentPlan", "POST", params).then(() => {
      Toast.success("计划已修改");
      setEditPlanVisible(false);
      detect();
    });
  }, [currentPlan, editOneTermAmount, editDueDate, editPaidAmount, editPaidDate, detect]);

  const openAddPlan = useCallback((loanId: number) => {
    setCurrentLoanId(loanId);
    setAddPeriodNo(undefined);
    setAddOneTermAmount(undefined);
    setAddDueDate(undefined);
    setAddPlanVisible(true);
  }, []);

  const saveAddPlan = useCallback(() => {
    if (!addPeriodNo || !addOneTermAmount || !addDueDate) {
      Toast.warning("请填写完整信息");
      return;
    }
    const params: any = { loanId: currentLoanId };
    params.periodNo = addPeriodNo;
    params.oneTermAmount = addOneTermAmount;
    params.dueDate = dayjs(addDueDate).format("YYYY-MM-DD");
    apiClient.request("/repaymentPlan/modifyRepaymentPlan", "POST", params).then(() => {
      Toast.success("计划已新增");
      setAddPlanVisible(false);
      detect();
    });
  }, [currentLoanId, addPeriodNo, addOneTermAmount, addDueDate, detect]);

  const getPlanColumns = useCallback((loanType: string): any[] => {
    const cols: any[] = [
      { title: "期号", dataIndex: "periodNo", width: 60 },
      { title: "应还日期", dataIndex: "dueDate", width: 100 },
      { title: "应还金额", dataIndex: "oneTermAmount", width: 90, render: (v: number) => formatAmountZhCn(v) },
      { title: "实还日期", dataIndex: "paidDate", width: 100 },
      {
        title: "实还金额", width: 100,
        render: (_: any, r: PlanRow) => formatAmountZhCn(r.paidAmount || 0),
      },
    ];
    if (loanType === "CARD") {
      cols.push({ title: "过账金额", dataIndex: "postingAmount", width: 100, render: (v: number) => formatAmountZhCn(v || 0) });
    }
    cols.push(
      {
        title: "状态", dataIndex: "status", width: 80,
        render: (v: string) => {
          const s = PLAN_STATUS_MAP[v] || { text: v, color: "grey" as TagColor };
          return <Tag size="small" color={s.color}>{s.text}</Tag>;
        },
      },
      {
        title: "异常", width: 150,
        render: (_: any, r: PlanRow) => (
          <span>{(r.anomalyTypes || []).map((t, i) => {
            const c = ANOMALY_CONFIG[t];
            return c ? <Tag key={i} size="small" color={c.color}>{c.label}</Tag> : null;
          })}</span>
        ),
      },
      {
        title: "操作", width: 100,
        render: (_: any, r: PlanRow) => (
          <Space>
            <Button size="small" onClick={() => openEditPlan(r)}>修改</Button>
            <Popconfirm title="确认删除此计划？" onConfirm={() => deletePlan(r.id)}>
              <Button size="small" type="danger">删除</Button>
            </Popconfirm>
          </Space>
        ),
      },
    );
    return cols;
  }, [openEditPlan, deletePlan]);

  const loanColumns: any[] = [
    { title: "贷款编号", dataIndex: "loanNo", width: 160 },
    { title: "客户", dataIndex: "leadName", width: 80 },
    { title: "类型", dataIndex: "loanType", width: 70, render: (v: string) => LOAN_TYPE_MAP[v] || v },
    { title: "平台", dataIndex: "platform", width: 80 },
    {
      title: "贷款状态", dataIndex: "loanStatus", width: 80,
      render: (v: string) => {
        const s = LOAN_STATUS_MAP[v] || { text: v || "", color: "grey" as TagColor };
        return <Tag size="small" color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: "贷款金额", dataIndex: "loanAmount", width: 100,
      render: (v: number) => <Text strong>{formatAmountZhCn(v)}</Text>,
    },
    { title: "计划总额", dataIndex: "planTotalAmount", width: 100, render: (v: number) => formatAmountZhCn(v) },
    { title: "实还总额", dataIndex: "paidTotalAmount", width: 100, render: (v: number) => formatAmountZhCn(v) },
    {
      title: "异常", width: 300,
      render: (_: any, r: LoanRow) => (
        <span>{r.anomalies.map((a, i) => {
          const c = ANOMALY_CONFIG[a.type] || { label: a.type, color: "grey" as TagColor };
          return <Tag key={i} color={c.color} style={{ marginBottom: 2 }}>{c.label}</Tag>;
        })}</span>
      ),
    },
    {
      title: "操作", width: 130, fixed: "right",
      render: (_: any, r: LoanRow) => (
        <Space>
          <Button size="small" onClick={() => openAddPlan(r.loanId)}>新增计划</Button>
          <Popconfirm title="确认删除此贷款及所有计划？" onConfirm={() => deleteLoan(r.loanId)}>
            <Button size="small" type="danger">删除贷款</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 8 }}>
        <Input showClear placeholder="客户ID" value={sLeadId} onChange={setSLeadId} style={{ width: 100 }} />
        <Input showClear placeholder="客户姓名" value={sLeadName} onChange={setSLeadName} style={{ width: 100 }} />
        <Input showClear placeholder="贷款编号" value={sLoanNo} onChange={setSLoanNo} style={{ width: 180 }} />
        <Select showClear placeholder="贷款类型" value={sLoanType || undefined} onChange={(v: string) => setSLoanType(v || "")}
          style={{ width: 110 }}  optionList={[
            { value: "MORTGAGE", label: "信贷" }, { value: "CARD", label: "信用卡" }, { value: "BORROW", label: "借款" },
          ]} />
        <Input showClear placeholder="按揭专员" value={sAgentName} onChange={setSAgentName} style={{ width: 100 }} />
        <DatePicker type="dateRange" showClear placeholder={["应还日期起", "应还日期止"]} value={sDueDateRange} onChange={(v: [Date, Date]) => setSDueDateRange(v)} style={{ width: 260 }} />
        <DatePicker type="dateRange" showClear placeholder={["实还日期起", "实还日期止"]} value={sPaidDateRange} onChange={(v: [Date, Date]) => setSPaidDateRange(v)} style={{ width: 260 }} />
        <Button icon={<IconSearch />} theme="solid" onClick={detect}>查询</Button>
        <Button icon={<IconSetting />} onClick={loadThresholds}>监控设置</Button>
      </div>
      <div style={{ marginBottom: 12 }}>
        <Text style={{ color: "#999" }}>检测到 {data.length} 条异常贷款</Text>
      </div>

      <Table
        columns={loanColumns}
        dataSource={data}
        rowKey="loanId"
        size="small"
        pagination={false}
        expandedRowRender={(record: LoanRow) => {
          if (!record.plans || record.plans.length === 0) return <Text type="tertiary">无计划数据</Text>;
          return (
            <div style={{ margin: "0 0 0 24px" }}>
              {record.anomalies.map((a, i) => (
                <div key={i} style={{ marginBottom: 6, padding: "6px 10px", background: "#fffbe6", borderRadius: 4, fontSize: 12 }}>
                  <Text strong style={{ color: a.severity === "high" ? "#e53935" : (a.severity === "medium" ? "#ef6c00" : "#1677ff") }}>
                    {ANOMALY_CONFIG[a.type]?.label || a.type}：
                  </Text>
                  <Text>{a.message}</Text>
                  <div style={{ color: "#666", marginTop: 2 }}>💡 {a.suggestion}</div>
                </div>
              ))}
              <Table columns={getPlanColumns(record.loanType)} dataSource={record.plans} rowKey="id" size="small" pagination={false} />
            </div>
          );
        }}
      />

      {/* 设置Modal */}
      <Modal title="异常监控设置" visible={thresholdVisible} onOk={saveThresholds} onCancel={() => setThresholdVisible(false)} style={{ minWidth: 600 }}>
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: 14, display: "block", marginBottom: 8 }}>启用场景</Text>
          <Text type="tertiary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>勾选需要监控的异常场景，未勾选的场景不会参与检测</Text>
          <Checkbox.Group
            value={enabledTypes}
            onChange={(v: string[]) => setEnabledTypes(v)}
            style={{ width: "100%" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {ANOMALY_SCENARIOS.map(s => (
                <div key={s.type} style={{ display: "flex", alignItems: "flex-start", padding: "6px 8px", background: "#fafafa", borderRadius: 4 }}>
                  <Checkbox value={s.type} style={{ marginRight: 8, marginTop: 2 }} />
                  <div>
                    <Tag size="small" color={s.color} style={{ marginRight: 6 }}>{s.label}</Tag>
                    <Text style={{ fontSize: 12 }}>{s.desc}</Text>
                  </div>
                </div>
              ))}
            </div>
          </Checkbox.Group>
        </div>

        <div style={{ borderTop: "1px solid #eee", paddingTop: 16 }}>
          <Text strong style={{ fontSize: 14, display: "block", marginBottom: 8 }}>阈值参数</Text>
          {[
            { key: "amountDeviationPercent", label: "金额偏差百分比(%)", tip: "计划总额与贷款金额的偏差阈值" },
            { key: "overdueDays", label: "逾期天数", tip: "超过应还日期多少天视为逾期" },
            { key: "earlyPaymentDays", label: "提前还款天数", tip: "实还日期早于应还日期多少天视为异常" },
            { key: "singlePeriodDeviationPercent", label: "单期偏差百分比(%)", tip: "单期实还金额与计划金额的偏差阈值" },
          ].map(item => (
            <div key={item.key} style={{ marginBottom: 12 }}>
              <div><Text style={{ fontSize: 13, fontWeight: 500 }}>{item.label}</Text>
              <Text style={{ fontSize: 11, color: "#999", marginLeft: 8 }}>{item.tip}</Text></div>
              <InputNumber
                style={{ width: "100%", marginTop: 4 }}
                value={thresholds[item.key] as number}
                onChange={(v: number) => setThresholds({ ...thresholds, [item.key]: v })}
              />
            </div>
          ))}
        </div>
      </Modal>

      {/* 修改计划Modal */}
      <Modal title="修改还款计划" visible={editPlanVisible} onOk={saveEditPlan} onCancel={() => setEditPlanVisible(false)}>
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>应还金额</Text>
          <InputNumber style={{ width: "100%" }} value={editOneTermAmount} onChange={(v: number) => setEditOneTermAmount(v)} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>应还日期</Text>
          <DatePicker style={{ width: "100%" }} value={editDueDate} onChange={(v: Date) => setEditDueDate(v)} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>实还金额</Text>
          <InputNumber style={{ width: "100%" }} value={editPaidAmount} onChange={(v: number) => setEditPaidAmount(v)} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>实还日期</Text>
          <DatePicker style={{ width: "100%" }} value={editPaidDate} onChange={(v: Date) => setEditPaidDate(v)} />
        </div>
      </Modal>

      {/* 新增计划Modal */}
      <Modal title="新增还款计划" visible={addPlanVisible} onOk={saveAddPlan} onCancel={() => setAddPlanVisible(false)}>
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>期号 *</Text>
          <InputNumber style={{ width: "100%" }} value={addPeriodNo} onChange={(v: number) => setAddPeriodNo(v)} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>应还金额 *</Text>
          <InputNumber style={{ width: "100%" }} value={addOneTermAmount} onChange={(v: number) => setAddOneTermAmount(v)} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>应还日期 *</Text>
          <DatePicker style={{ width: "100%" }} value={addDueDate} onChange={(v: Date) => setAddDueDate(v)} />
        </div>
      </Modal>
    </Spin>
  );
}
